import { useLayoutEffect, useRef } from 'react';

import type { SkillCommandCandidate } from './AIAssistantSkillCommands';
import { scrollOptionIntoView } from './scrollOptionIntoView';

export const AI_ASSISTANT_SKILL_COMMAND_LISTBOX_ID = 'ai-assistant-skill-command-listbox';

export function getSkillCommandOptionId(index: number): string {
  return `ai-assistant-skill-command-option-${index}`;
}

export function AIAssistantSkillCommandMenu({
  candidates,
  highlightedIndex,
  label,
  argumentLabel,
  onHighlight,
  onSelect,
}: {
  candidates: readonly SkillCommandCandidate[];
  highlightedIndex: number;
  label: string;
  argumentLabel: string;
  onHighlight: (index: number) => void;
  onSelect: (candidate: SkillCommandCandidate) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // マウス操作で選択した候補までスクロールすると、カーソル下の候補が入れ替わって選択が暴れるため、
  // マウス起因で選択した候補を覚えておき、その候補についてはスクロールしない。
  const pointerHighlightedIndexRef = useRef<number | null>(null);

  // キーボード操作で選択が移った候補が隠れないように、リスト自体をスクロールさせる。
  // paint 前に位置を合わせてちらつきを防ぐため useLayoutEffect を使う。
  useLayoutEffect(() => {
    const pointerHighlightedIndex = pointerHighlightedIndexRef.current;
    pointerHighlightedIndexRef.current = null;
    if (pointerHighlightedIndex === highlightedIndex) return;

    const list = listRef.current;
    const option = optionRefs.current[highlightedIndex];
    if (!list || !option) return;

    scrollOptionIntoView(list, option);
  }, [highlightedIndex, candidates]);

  return (
    <div
      ref={listRef}
      id={AI_ASSISTANT_SKILL_COMMAND_LISTBOX_ID}
      role="listbox"
      aria-label={label}
      className="absolute inset-x-0 bottom-full z-20 mb-1 max-h-64 overflow-y-auto rounded border border-gray-200 bg-white py-1 shadow-lg"
    >
      {candidates.map((candidate, index) => {
        const isHighlighted = index === highlightedIndex;
        return (
          <button
            key={`${candidate.skill.name}\0${candidate.trigger}`}
            ref={(element) => {
              optionRefs.current[index] = element;
            }}
            id={getSkillCommandOptionId(index)}
            type="button"
            role="option"
            aria-selected={isHighlighted}
            className={`block w-full px-3 py-2 text-left ${
              isHighlighted ? 'bg-blue-50 text-blue-900' : 'text-gray-800 hover:bg-gray-50'
            }`}
            // スクロールでカーソル下の候補が入れ替わると mouseenter が発火してキーボードでの選択を
            // 奪ってしまうため、実際にマウスが動いた時だけ選択する mousemove を使う。
            onMouseMove={() => {
              if (isHighlighted) return;
              pointerHighlightedIndexRef.current = index;
              onHighlight(index);
            }}
            onMouseDown={(event) => {
              // click より先に textarea が blur するのを防ぎ、候補確定後も入力を継続できるようにする。
              event.preventDefault();
            }}
            onClick={() => onSelect(candidate)}
          >
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-sm font-semibold">{candidate.trigger}</span>
              <span className="truncate text-xs text-gray-500">{candidate.skill.name}</span>
            </div>
            {candidate.skill.description && (
              <div className="mt-0.5 line-clamp-2 text-xs text-gray-600">{candidate.skill.description}</div>
            )}
            {candidate.skill.argumentHint && (
              <div className="mt-0.5 truncate text-xs text-gray-400">
                {argumentLabel}: {candidate.skill.argumentHint}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
